import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import type { UserProfile } from '@types';
import { db } from '../../../firebaseConfig';
import SupportChatModal, { type SupportChatRecord } from './SupportChatModal';

const ManagementChatNotifications: React.FC<{ currentUser: UserProfile }> = ({ currentUser }) => {
    const [notification, setNotification] = useState<SupportChatRecord | null>(null);
    const [openChat, setOpenChat] = useState<SupportChatRecord | null>(null);
    const [listenerError, setListenerError] = useState('');
    const seenMessages = useRef(new Map<string, number>());

    useEffect(() => {
        const callable = httpsCallable(getFunctions(undefined, 'us-central1'), 'manageSupportChat');
        const update = () => void callable({ action: 'updatePresence', data: { visible: document.visibilityState === 'visible' } }).catch(() => undefined);
        update();
        const timer = window.setInterval(update, 60000);
        document.addEventListener('visibilitychange', update);
        return () => {
            window.clearInterval(timer);
            document.removeEventListener('visibilitychange', update);
            void callable({ action: 'updatePresence', data: { visible: false } }).catch(() => undefined);
        };
    }, []);

    useEffect(() => {
        const constraints = [where('managerUid', '==', currentUser.uid)];
        const chatsQuery = query(collection(db, 'support_chats'), ...constraints);
        return onSnapshot(chatsQuery, snapshot => {
            setListenerError('');
            const previous = seenMessages.current;
            const recentCutoff = Date.now() - 5 * 60 * 1000;
            const unread = snapshot.docs
                .map(document => ({ id: document.id, ...document.data() } as SupportChatRecord))
                .filter(chat => chat.unreadForManager && chat.lastMessageSenderType === 'client')
                .map(chat => ({ chat, sentAt: chat.lastMessageAt?.toMillis?.() || 0 }))
                .filter(item => item.sentAt >= recentCutoff && item.sentAt > (previous.get(item.chat.id) || 0))
                .sort((left, right) => right.sentAt - left.sentAt);
            const next = new Map<string, number>();
            for (const document of snapshot.docs)
                next.set(document.id, document.data().lastMessageAt?.toMillis?.() || 0);
            seenMessages.current = next;
            if (unread[0])
                setNotification(unread[0].chat);
        }, error => {
            console.error('Support chat notification listener failed.', error);
            setListenerError('Chat notifications could not connect.');
        });
    }, [currentUser.uid]);

    return createPortal(
        <>
            {listenerError && <div className="fixed bottom-6 right-6 z-[1000] w-80 rounded-xl bg-red-700 px-4 py-3 text-sm font-semibold text-white shadow-2xl">{listenerError}</div>}
            {notification && (
                <button type="button" onClick={() => { setOpenChat(notification); setNotification(null); }} className="fixed bottom-6 right-6 z-[1000] w-80 rounded-2xl border border-blue-400 bg-slate-900 p-4 text-left text-white shadow-2xl hover:bg-slate-800">
                    <div className="flex items-start justify-between gap-3"><div><p className="text-[10px] font-bold uppercase tracking-widest text-blue-300">New client chat</p><p className="mt-1 font-extrabold">{notification.participantName}</p></div><span onClick={event => { event.stopPropagation(); setNotification(null); }} className="rounded px-2 text-lg text-slate-400 hover:bg-white/10 hover:text-white">×</span></div>
                    <p className="mt-1 text-xs text-slate-300">{notification.organizationName} · {notification.region}</p>
                    <p className="mt-3 line-clamp-2 text-sm text-slate-100">{notification.lastMessagePreview}</p>
                    <p className="mt-3 text-xs font-bold text-blue-300">Open conversation →</p>
                </button>
            )}
            {openChat && <SupportChatModal mode="admin" chat={openChat} onClose={() => setOpenChat(null)} />}
        </>,
        document.body,
    );
};

export default ManagementChatNotifications;
